<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import { PictureOutlined } from '@ant-design/icons-vue';
import EditTitleModal from '../EditTitleModal.vue';
import EditDescriptionModal from '../EditDescriptionModal.vue';
import ImageUploadModal from '../ImageUploadModal.vue';

const b = bem('info-background-module');

export default {
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    ImageUploadModal,
    PictureOutlined,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      isTitleModalVisible: false,
      isDescriptionModalVisible: false,
      isImageModalVisible: false,
    };
  },
  computed: {
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    contentPosition() {
      const templateId = this.moduleData?.templateId
        || this.moduleData?.parsedContent?.templateId
        || 'info-background-right';
      if (templateId === 'info-background-left') {
        return 'left';
      }
      return 'right';
    },
    displayTitle() {
      if (this.isMobile && this.content.titleMobile) {
        return this.content.titleMobile;
      }
      return this.content.title || 'Title';
    },
    displayDescription() {
      if (this.isMobile && this.content.descriptionMobile) {
        return this.content.descriptionMobile;
      }
      return this.content.description || 'Description';
    },
    displayImage() {
      if (this.isMobile && this.content.imageMobile) {
        return this.content.imageMobile;
      }
      return this.content.image || '';
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    openTitleModal() {
      if (this.isEditMode) {
        this.isTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.isDescriptionModalVisible = true;
      }
    },
    openImageModal() {
      if (this.isEditMode) {
        this.isImageModalVisible = true;
      }
    },
    handleTitleSave(data) {
      this.updateContent({
        ...this.content,
        title: data.title,
        titleMobile: data.titleMobile,
      });
      this.isTitleModalVisible = false;
    },
    handleDescriptionSave(data) {
      this.updateContent({
        ...this.content,
        description: data.description,
        descriptionMobile: data.descriptionMobile,
      });
      this.isDescriptionModalVisible = false;
    },
    handleImageSave(data) {
      this.updateContent({
        ...this.content,
        image: data.image,
        imageMobile: data.imageMobile,
      });
      this.isImageModalVisible = false;
    },
    updateContent(newContent) {
      this.$emit('update', {
        ...this.moduleData,
        content: JSON.stringify(newContent),
      });
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup, editable: isEditMode })">
    <div
      :class="b('placeholder', {
        'icon-left': contentPosition === 'right',
        'icon-right': contentPosition === 'left',
        editable: isEditMode,
        mobile: isMobile,
      })"
      role="button"
      tabindex="0"
      @click="openImageModal"
      @keydown.enter="openImageModal"
    >
      <img
        v-if="displayImage"
        :src="displayImage"
        :alt="displayTitle || 'Banner image'"
        :class="b('placeholder-image', { mobile: isMobile })"
      />
      <span v-if="!displayImage && !isPopup" :class="b('placeholder-text')">
        <span :class="b('placeholder-plus')">+</span><br>CLICK TO UPLOAD
      </span>
      <span v-if="!displayImage && isPopup" :class="b('placeholder-text')">
        <PictureOutlined />
      </span>
      <div
        v-if="isMobile"
        :class="b('title-row', { mobile: true })"
        role="button"
        tabindex="0"
        @click="openTitleModal"
        @keydown.enter="openTitleModal"
      >
        <span :class="b('title', { mobile: true })">{{ displayTitle }}</span>
      </div>
    </div>

    <div
      :class="b('dark-block', {
        left: contentPosition === 'left',
        popup: isPopup,
        mobile: isMobile
      })">
      <div
        v-if="!isMobile"
        :class="b('title-row', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openTitleModal"
        @keydown.enter="openTitleModal"
      >
        <span :class="b('title')">{{ displayTitle }}</span>
        <PencilIcon v-if="isEditMode" :size="20" color="#fff" :class="b('edit-icon')" />
      </div>

      <div
        :class="b('desc-row', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openDescriptionModal"
        @keydown.enter="openDescriptionModal"
      >
        <span :class="b('desc', { mobile: isMobile })">{{ displayDescription }}</span>
        <PencilIcon v-if="isEditMode" :size="16" color="#fff" :class="b('edit-icon')" />
      </div>
    </div>

    <EditTitleModal
      :visible="isTitleModalVisible"
      :title="content.title"
      :title-mobile="content.titleMobile"
      @close="isTitleModalVisible = false"
      @save="handleTitleSave"
    />

    <EditDescriptionModal
      :visible="isDescriptionModalVisible"
      :description="content.description"
      :description-mobile="content.descriptionMobile"
      @close="isDescriptionModalVisible = false"
      @save="handleDescriptionSave"
    />

    <ImageUploadModal
      :visible="isImageModalVisible"
      :image="content.image"
      :image-mobile="content.imageMobile"
      @close="isImageModalVisible = false"
      @save="handleImageSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.info-background-module {
  position: relative;
  width: 1540px;
  height: 608px;
  border-radius: 24px;
  overflow: hidden;
  margin: 64px 0;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    height: vw-full(608);
    border-radius: vw-full(24);
    margin: vw-full(64) 0;
  }

  &_popup {
    width: 1200px;
    height: 474px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
      height: vw-full(474);
    }
  }
  &_mobile {
    width: 412px;
    height: fit-content;
    margin: 40px 0;
    padding: 0 16px;
    gap: 20px;
  }
  &_editable {
    margin: 0;
  }

  &__placeholder {
    position: absolute;
    inset: 0;
    background: #d9d9d9;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;
    padding: 0;

    &_editable:hover {
      background: #cecece;
    }

    &_icon-left {
      justify-content: center;
      padding-right: calc(612px + 16px);

      @include mediaMinMax(1025, $normal) {
        padding-right: calc(#{vw-full(612)} + #{vw-full(16)});
      }
    }

    &_icon-right {
      justify-content: center;
      padding-left: calc(612px + 16px);

      @include mediaMinMax(1025, $normal) {
        padding-left: calc(#{vw-full(612)} + #{vw-full(16)});
      }
    }

    &_mobile {
      width: 380px;
      height: 554px;
      border-radius: 12px;
      position: relative;
      padding: 0;
    }
  }

  &__placeholder-text {
    font-family: Montserrat, sans-serif;
    font-size: 31px;
    font-weight: 400;
    color: #7f7f7f;
    text-align: center;
    line-height: 1.4;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(31);
    }
  }

  &__placeholder-plus {
    font-size: 72px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(72);
    }
  }

  &__dark-block {
    position: absolute;
    top: 16px;
    right: 16px;
    bottom: 16px;
    width: 612px;
    height: 576px;
    background: #5e5d5d;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    padding: 26px;
    border-radius: 16px;

    @include mediaMinMax(1025, $normal) {
      top: vw-full(16);
      right: vw-full(16);
      bottom: vw-full(16);
      width: vw-full(612);
      height: vw-full(576);
      gap: vw-full(32);
      padding: vw-full(26);
      border-radius: vw-full(16);
    }

    &_left {
      right: auto;
      left: 16px;

      @include mediaMinMax(1025, $normal) {
        left: vw-full(16);
      }
    }
    &_popup {
      top: 12px;
      right: 12px;
      bottom: 12px;
      width: 477px;
      height: 450px;
      gap: 20px;
      padding: 20px;
      border-radius: 16px;

      @include mediaMinMax(1025, $normal) {
        top: vw-full(12);
        right: vw-full(12);
        bottom: vw-full(12);
        width: vw-full(477);
        height: vw-full(450);
        gap: vw-full(20);
        padding: vw-full(20);
        border-radius: vw-full(16);
      }

      &_left {
        right: auto;
        left: 12px;

        @include mediaMinMax(1025, $normal) {
          left: vw-full(12);
        }
      }
    }
    &_mobile {
      position: initial;
      background-color: transparent;
      width: 100%;
      height: fit-content;
      padding: 0 12px;
      gap: 20px;
      margin-top: 20px;
    }
  }

  &__title-row,
  &__desc-row {
    display: flex;
    align-items: center;
    gap: 10px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(10);
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  &__placeholder-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    border-radius: inherit;
    pointer-events: none;

    &_mobile {
      border-radius: inherit;
    }
  }

  &__title-row {
    &_mobile {
      position: absolute;
      width: 100%;
      bottom: 16px;
      background: rgba(255, 255, 255, 0.80);
      padding: 26px 12px;
      text-align: center;
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 40px;
    font-weight: 600;
    color: #fff;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    &_mobile {
      width: 100%;
      color: #293241;
      font-size: 24px;
    }
  }

  &__desc {
    font-family: Montserrat, sans-serif;
    font-size: 19px;
    font-weight: 400;
    color: #fff;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }
    &_mobile {
      color: #5e5d5d;
    }
  }

  &__edit-icon {
    color: #fff;
    flex-shrink: 0;
  }
}
</style>
